import { Box, CloseButton, HStack, Portal, Text, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import type { BackfillMatchesStatusResponse } from '../api/sds-consumer/get-backfill-matches-status'
import { backfillMatchesStatusQuery } from '../api/sds-consumer/get-backfill-matches-status'
import * as spotfixRefreshStatusApi from '../api/sds-consumer/get-spotfix-tournament-season-refresh-status'
import {
  getSdsConsumerAsyncJobKey,
  isSdsConsumerAsyncJobRunning,
  type SdsConsumerAsyncJobState,
  type SdsConsumerAsyncJobStatus,
} from '../api/sds-consumer/job-types'

function formatAsyncJobTimestamp(value: string | null): string {
  if (!value) return '—'

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

function getAsyncJobStatusColor(status: SdsConsumerAsyncJobStatus): string {
  switch (status) {
    case 'running':
      return 'yellow.300'
    case 'completed':
      return 'green.300'
    case 'failed':
      return 'red.300'
    default:
      return 'gray.400'
  }
}

function JobIndicatorCard({
  job,
  title,
  onDismiss,
}: {
  job: SdsConsumerAsyncJobState
  title: string
  onDismiss: () => void
}) {
  return (
    <Box
      bg="gray.800"
      borderWidth="1px"
      borderColor="gray.600"
      borderRadius="lg"
      boxShadow="xl"
      color="white"
      p={4}
      w="360px"
    >
      <HStack align="start" justify="space-between" mb={2}>
        <VStack align="start" spacing={0}>
          <Text fontSize="sm" fontWeight="bold">
            {title}
          </Text>
          <Text fontSize="sm">
            Status:{' '}
            <Text as="span" color={getAsyncJobStatusColor(job.status)} fontWeight="bold">
              {job.status}
            </Text>
          </Text>
        </VStack>
        <CloseButton aria-label={`Dismiss ${title}`} onClick={onDismiss} />
      </HStack>

      <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
        Trace ID: {job.traceId || '—'}
      </Text>
      <Text fontSize="xs">Started: {formatAsyncJobTimestamp(job.startedAt)}</Text>
      <Text fontSize="xs">Finished: {formatAsyncJobTimestamp(job.finishedAt)}</Text>
      <Text fontSize="xs">Duration: {typeof job.durationMs === 'number' ? `${job.durationMs} ms` : '—'}</Text>

      {job.status === 'completed' && job.finishedAt && (
        <Text fontSize="xs" color="green.300" mt={2}>
          Completed at: {formatAsyncJobTimestamp(job.finishedAt)}
        </Text>
      )}

      {job.status === 'failed' && job.error && (
        <Text fontSize="xs" color="red.300" mt={2} noOfLines={3}>
          {job.error}
        </Text>
      )}
    </Box>
  )
}

export function SdsConsumerJobIndicator() {
  const [refreshJob, setRefreshJob] = useState<SdsConsumerAsyncJobState | null>(null)
  const [backfillJob, setBackfillJob] = useState<SdsConsumerAsyncJobState | null>(null)
  const [dismissedRefreshJobKey, setDismissedRefreshJobKey] = useState<string | null>(null)
  const [dismissedBackfillJobKey, setDismissedBackfillJobKey] = useState<string | null>(null)
  const hasSeenInitialRefreshJob = useRef(false)
  const hasSeenInitialBackfillJob = useRef(false)
  const trackedRefreshJobKey = useRef<string | null>(null)
  const trackedBackfillJobKey = useRef<string | null>(null)

  const refreshStatus = useQuery<spotfixRefreshStatusApi.SpotfixTournamentSeasonRefreshStatusResponse, Error>({
    ...spotfixRefreshStatusApi.spotfixTournamentSeasonRefreshStatusQuery(),
    refetchInterval: data => (data?.job.status === 'running' ? 3000 : false),
    refetchOnWindowFocus: true,
  })

  const backfillStatus = useQuery<BackfillMatchesStatusResponse, Error>({
    ...backfillMatchesStatusQuery(),
    refetchInterval: data => (data?.job.status === 'running' ? 3000 : false),
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    const job = refreshStatus.data?.job
    if (!job) return

    const jobKey = getSdsConsumerAsyncJobKey(job)

    if (!hasSeenInitialRefreshJob.current) {
      hasSeenInitialRefreshJob.current = true

      if (isSdsConsumerAsyncJobRunning(job)) {
        trackedRefreshJobKey.current = jobKey
        setRefreshJob(job)
      } else {
        trackedRefreshJobKey.current = null
        setRefreshJob(null)
      }

      return
    }

    if (job.status === 'idle') {
      setRefreshJob(currentJob => (currentJob && !isSdsConsumerAsyncJobRunning(currentJob) ? currentJob : null))
      return
    }

    if (isSdsConsumerAsyncJobRunning(job)) {
      trackedRefreshJobKey.current = jobKey
      setRefreshJob(job)
      return
    }

    if (trackedRefreshJobKey.current === jobKey) {
      setRefreshJob(job)
      return
    }

    setRefreshJob(null)
  }, [refreshStatus.data])

  useEffect(() => {
    const job = backfillStatus.data?.job
    if (!job) return

    const jobKey = getSdsConsumerAsyncJobKey(job)

    if (!hasSeenInitialBackfillJob.current) {
      hasSeenInitialBackfillJob.current = true

      if (isSdsConsumerAsyncJobRunning(job)) {
        trackedBackfillJobKey.current = jobKey
        setBackfillJob(job)
      } else {
        trackedBackfillJobKey.current = null
        setBackfillJob(null)
      }

      return
    }

    if (job.status === 'idle') {
      setBackfillJob(currentJob => (currentJob && !isSdsConsumerAsyncJobRunning(currentJob) ? currentJob : null))
      return
    }

    if (isSdsConsumerAsyncJobRunning(job)) {
      trackedBackfillJobKey.current = jobKey
      setBackfillJob(job)
      return
    }

    if (trackedBackfillJobKey.current === jobKey) {
      setBackfillJob(job)
      return
    }

    setBackfillJob(null)
  }, [backfillStatus.data])

  const refreshJobKey = refreshJob ? getSdsConsumerAsyncJobKey(refreshJob) : null
  const showRefreshJob = Boolean(refreshJob && refreshJobKey !== dismissedRefreshJobKey)

  const backfillJobKey = backfillJob ? getSdsConsumerAsyncJobKey(backfillJob) : null
  const showBackfillJob = Boolean(backfillJob && backfillJobKey !== dismissedBackfillJobKey)

  if (!showRefreshJob && !showBackfillJob) {
    return null
  }

  return (
    <Portal>
      <VStack align="stretch" position="fixed" right={4} spacing={3} top={4} zIndex={1400}>
        {showRefreshJob && refreshJob && refreshJobKey && (
          <JobIndicatorCard
            job={refreshJob}
            title="SDS Refresh"
            onDismiss={() => setDismissedRefreshJobKey(refreshJobKey)}
          />
        )}

        {showBackfillJob && backfillJob && backfillJobKey && (
          <JobIndicatorCard
            job={backfillJob}
            title="SDS Backfill"
            onDismiss={() => setDismissedBackfillJobKey(backfillJobKey)}
          />
        )}
      </VStack>
    </Portal>
  )
}
